import { useTranslation } from 'react-i18next';
import EmptyCell from 'components/EmptyCell';
import { statusCategoryClass } from 'pages/AdminIssues/components/columns';
import type { FollowUpRequest } from 'api/interventionRequestsApi';

interface JiraStatusPillProps {
  request: FollowUpRequest;
  /** The page's `meta.jiraStatusAvailable`: tells a failed lookup (`—`) from a vanished key. */
  available: boolean;
}

/**
 * The live Jira status (E17), the pill of « Tous les tickets ». The name is Jira's own, never
 * translated. The amber dot flags the one failure case: accepted here, still *new* in Jira.
 */
const JiraStatusPill = ({ request, available }: JiraStatusPillProps) => {
  const { t } = useTranslation();
  const { jiraStatus, status } = request;

  if (!jiraStatus) {
    return available ? (
      <span className="whitespace-nowrap text-[13px] text-muted-foreground">{t('Off GPS')}</span>
    ) : (
      <EmptyCell />
    );
  }

  const lagging = status === 'accepted' && jiraStatus.category === 'new';
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span
        title={jiraStatus.name}
        className={`inline-block max-w-[150px] truncate rounded-md px-2 py-0.5 align-middle text-xs font-medium ${
          statusCategoryClass[jiraStatus.category] ?? ''
        }`}
      >
        {jiraStatus.name}
      </span>
      {lagging && (
        <span
          className="h-2 w-2 shrink-0 rounded-full bg-amber-500 dark:bg-amber-400"
          role="img"
          aria-label={t("Jira hasn't caught up yet")}
          title={t("Jira hasn't caught up yet")}
        />
      )}
    </span>
  );
};

export default JiraStatusPill;
